import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Box, Button, CircularProgress, Container,
  Dialog, DialogActions, DialogContent, DialogTitle,
  IconButton, Paper, Stack, TextField, Typography,
} from "@mui/material";
import { MdAdd, MdCloudUpload, MdDelete, MdEdit, MdRefresh, MdSave } from "react-icons/md";
import { db, storage } from "../../firebase/firebase-config";
import Colors from "../../assets/styles/Colors";

const ITEMS_PATH = () =>
  db.collection("businesses").doc("root").collection("items");

// ── Helpers ────────────────────────────────────────────────────────────────────

async function fetchGettingStarted() {
  const snap = await ITEMS_PATH()
    .where("category", "==", "getting_started")
    .orderBy("orderNumber", "asc")
    .get();
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

async function uploadImage(file, itemId) {
  const ext  = file.name.split(".").pop();
  const ref  = storage.ref().child(`getting_started/${itemId}_${Date.now()}.${ext}`);
  await ref.put(file);
  return ref.getDownloadURL();
}

async function saveItem(data, file, existingId) {
  let imageURL = data.imageURL || "";
  const docId  = existingId || ITEMS_PATH().doc().id;

  if (file) {
    imageURL = await uploadImage(file, docId);
  }

  await ITEMS_PATH().doc(docId).set(
    {
      name:        data.name        || "",
      description: data.description || "",
      imageURL,
      clickMe:     data.clickMe     || "",
      orderNumber: Number(data.orderNumber) || 0,
      category:    "getting_started",
      isActive:    true,
      updatedAt:   new Date().toISOString(),
    },
    { merge: true }
  );
}

async function deleteItem(id) {
  await ITEMS_PATH().doc(id).delete();
}

// ── ItemForm (add / edit dialog) ───────────────────────────────────────────────

function ItemForm({ open, onClose, initial, onSaved }) {
  const blank = { name: "", description: "", imageURL: "", clickMe: "", orderNumber: 0 };
  const [form,    setForm]    = useState(blank);
  const [file,    setFile]    = useState(null);
  const [preview, setPreview] = useState(null);
  const [saving,  setSaving]  = useState(false);
  const [error,   setError]   = useState("");
  const fileRef = useRef();

  useEffect(() => {
    if (open) {
      setForm(initial ? { ...blank, ...initial } : blank);
      setFile(null);
      setPreview(initial?.imageURL || null);
      setError("");
    }
  }, [open, initial]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    return () => { if (preview?.startsWith("blob:")) URL.revokeObjectURL(preview); };
  }, [preview]);

  const handle = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

  const pickFile = (f) => {
    if (!f) return;
    if (preview?.startsWith("blob:")) URL.revokeObjectURL(preview);
    setFile(f);
    setPreview(URL.createObjectURL(f));
  };

  const handleSave = async () => {
    if (!form.name.trim()) { setError("El nombre es obligatorio."); return; }
    setSaving(true);
    setError("");
    try {
      await saveItem(form, file, initial?.id);
      onSaved();
      onClose();
    } catch (e) {
      setError("Error al guardar. Intenta de nuevo.");
    } finally {
      setSaving(false);
    }
  };

  const inputSx = {
    "& .MuiOutlinedInput-root": {
      backgroundColor: Colors.bgTertiary,
      color: Colors.textPrimary,
      "& fieldset": { borderColor: Colors.borderSecondary },
      "&:hover fieldset": { borderColor: Colors.borderPrimary },
    },
    "& .MuiInputLabel-root": { color: Colors.textSecondary },
  };

  return (
    <Dialog
      open={open}
      onClose={() => !saving && onClose()}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { backgroundColor: Colors.bgSecondary, border: `1px solid ${Colors.borderSecondary}` } }}
    >
      <DialogTitle sx={{ color: Colors.textPrimary }}>
        {initial ? "Editar paso" : "Nuevo paso"}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 0.5 }}>
          {/* Image drop zone */}
          <Box
            onDrop={(e) => { e.preventDefault(); pickFile(e.dataTransfer.files?.[0]); }}
            onDragOver={(e) => e.preventDefault()}
            onClick={() => fileRef.current.click()}
            sx={{
              border: `2px dashed ${Colors.borderPrimary}`,
              borderRadius: 2,
              height: 160,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              overflow: "hidden",
              backgroundColor: Colors.bgTertiary,
              transition: "border-color 160ms",
              "&:hover": { borderColor: Colors.primary },
            }}
          >
            {preview ? (
              <img src={preview} alt="preview" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
            ) : (
              <Stack alignItems="center" spacing={0.75}>
                <MdCloudUpload size={24} color={Colors.textSecondary} />
                <Typography variant="caption" sx={{ color: Colors.textSecondary }}>Arrastra o haz clic</Typography>
              </Stack>
            )}
            <input ref={fileRef} type="file" accept="image/*" style={{ display: "none" }}
              onChange={(e) => pickFile(e.target.files?.[0])} />
          </Box>

          <TextField label="Nombre / Título *" value={form.name} onChange={handle("name")} fullWidth size="small" sx={inputSx} />
          <TextField label="Descripción" value={form.description} onChange={handle("description")} fullWidth size="small" multiline minRows={2} sx={inputSx} />
          <TextField label="URL de destino" value={form.clickMe} onChange={handle("clickMe")} fullWidth size="small" placeholder="https://..." sx={inputSx} />
          <TextField label="Orden" value={form.orderNumber} onChange={handle("orderNumber")} fullWidth size="small" type="number" inputProps={{ min: 0 }} sx={inputSx} />

          {error && <Typography variant="caption" sx={{ color: Colors.error }}>{error}</Typography>}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={saving} sx={{ color: Colors.textSecondary, textTransform: "none" }}>Cancelar</Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving}
          startIcon={saving ? <CircularProgress size={16} sx={{ color: Colors.white }} /> : <MdSave />}
          sx={{ backgroundColor: Colors.primary, color: Colors.white, textTransform: "none", borderRadius: 2 }}
        >
          {saving ? "Guardando..." : "Guardar"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ── PrimerosPasosView ─────────────────────────────────────────────────────────

export default function PrimerosPasosView() {
  const [items,        setItems]        = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [addOpen,      setAddOpen]      = useState(false);
  const [editItem,     setEditItem]     = useState(null);
  const [confirmDel,   setConfirmDel]   = useState(null);
  const [deleting,     setDeleting]     = useState(false);
  const isFirstLoad = useRef(true);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      setItems(await fetchGettingStarted());
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isFirstLoad.current) {
      isFirstLoad.current = false;
      refresh();
    }
  }, [refresh]);

  const confirmDelete = async () => {
    if (!confirmDel) return;
    setDeleting(true);
    try {
      await deleteItem(confirmDel.id);
      setConfirmDel(null);
      refresh();
    } catch (e) {
      console.error(e);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Box sx={{ width: "100%", py: 3 }}>
      <Container maxWidth="xl">

        {/* Header */}
        <Paper sx={{ mb: 3, p: 2.5, borderRadius: 3, background: `linear-gradient(135deg, ${Colors.bgSecondary} 0%, ${Colors.bgTertiary} 100%)`, border: `1px solid ${Colors.borderSecondary}` }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Box>
              <Typography variant="h5" sx={{ color: Colors.textPrimary }}>Primeros Pasos</Typography>
              <Typography variant="body2" sx={{ color: Colors.textSecondary }}>
                Tarjetas de la sección "Ruta de inicio" en la pantalla de inicio
              </Typography>
            </Box>
            <Stack direction="row" spacing={1.5}>
              <IconButton onClick={refresh} sx={{ color: Colors.textSecondary }}><MdRefresh size={20} /></IconButton>
              <Button
                variant="contained"
                startIcon={<MdAdd />}
                onClick={() => setAddOpen(true)}
                sx={{ backgroundColor: Colors.primary, color: Colors.white, textTransform: "none", borderRadius: 2 }}
              >
                Nuevo Paso
              </Button>
            </Stack>
          </Stack>
        </Paper>

        {loading && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <CircularProgress sx={{ color: Colors.primary }} />
          </Box>
        )}

        {!loading && (
          <Paper sx={{ borderRadius: 3, backgroundColor: Colors.bgSecondary, border: `1px solid ${Colors.borderSecondary}`, overflow: "hidden" }}>
            {/* Table header */}
            <Box sx={{ px: 3, py: 1.5, backgroundColor: Colors.bgTertiary, borderBottom: `1px solid ${Colors.borderSecondary}` }}>
              <Stack direction="row" spacing={2} alignItems="center">
                <Box sx={{ width: 64, flexShrink: 0 }}><Typography variant="subtitle2" sx={{ color: Colors.textSecondary }}>Imagen</Typography></Box>
                <Box sx={{ flex: 2 }}><Typography variant="subtitle2" sx={{ color: Colors.textSecondary }}>Nombre</Typography></Box>
                <Box sx={{ flex: 3 }}><Typography variant="subtitle2" sx={{ color: Colors.textSecondary }}>Descripción</Typography></Box>
                <Box sx={{ width: 60, flexShrink: 0 }}><Typography variant="subtitle2" sx={{ color: Colors.textSecondary }}>Orden</Typography></Box>
                <Box sx={{ width: 90, flexShrink: 0 }}><Typography variant="subtitle2" sx={{ color: Colors.textSecondary }}>Acciones</Typography></Box>
              </Stack>
            </Box>

            {items.length === 0 && (
              <Box sx={{ px: 3, py: 4, textAlign: "center" }}>
                <Typography sx={{ color: Colors.textSecondary }}>Sin pasos aún. Agrega el primer paso.</Typography>
              </Box>
            )}

            {items.map((item) => (
              <Box
                key={item.id}
                sx={{
                  px: 3, py: 1.75,
                  borderBottom: `1px solid ${Colors.borderSecondary}`,
                  "&:hover": { backgroundColor: Colors.bgTertiary },
                  transition: "background-color 0.15s",
                }}
              >
                <Stack direction="row" spacing={2} alignItems="center">
                  <Box sx={{ width: 64, flexShrink: 0 }}>
                    {item.imageURL
                      ? <img src={item.imageURL} alt="" width={52} height={36} style={{ borderRadius: 6, objectFit: "cover", display: "block" }} />
                      : <Box sx={{ width: 52, height: 36, borderRadius: 1, backgroundColor: Colors.bgTertiary, border: `1px solid ${Colors.borderSecondary}` }} />
                    }
                  </Box>
                  <Box sx={{ flex: 2 }}>
                    <Typography variant="body2" sx={{ color: Colors.textPrimary, fontWeight: 600 }}>{item.name || "—"}</Typography>
                  </Box>
                  <Box sx={{ flex: 3, overflow: "hidden" }}>
                    <Typography
                      variant="caption"
                      sx={{ color: Colors.textSecondary, display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                    >
                      {item.description || "—"}
                    </Typography>
                  </Box>
                  <Box sx={{ width: 60, flexShrink: 0 }}>
                    <Typography variant="body2" sx={{ color: Colors.textSecondary }}>{item.orderNumber ?? "—"}</Typography>
                  </Box>
                  <Stack direction="row" spacing={0.5} sx={{ width: 90, flexShrink: 0 }}>
                    <IconButton size="small" onClick={() => setEditItem(item)} sx={{ color: Colors.textSecondary }} title="Editar">
                      <MdEdit size={16} />
                    </IconButton>
                    <IconButton size="small" onClick={() => setConfirmDel(item)} sx={{ color: Colors.error }} title="Eliminar">
                      <MdDelete size={16} />
                    </IconButton>
                  </Stack>
                </Stack>
              </Box>
            ))}
          </Paper>
        )}

        {/* Add dialog */}
        <ItemForm open={addOpen} onClose={() => setAddOpen(false)} initial={null} onSaved={refresh} />

        {/* Edit dialog */}
        {editItem && (
          <ItemForm open={!!editItem} onClose={() => setEditItem(null)} initial={editItem} onSaved={refresh} />
        )}

        {/* Confirm delete */}
        <Dialog
          open={!!confirmDel}
          onClose={() => !deleting && setConfirmDel(null)}
          PaperProps={{ sx: { backgroundColor: Colors.bgSecondary, border: `1px solid ${Colors.borderSecondary}` } }}
        >
          <DialogTitle sx={{ color: Colors.textPrimary }}>Confirmar eliminación</DialogTitle>
          <DialogContent>
            <Typography sx={{ color: Colors.textSecondary }}>
              ¿Eliminar "{confirmDel?.name}"? Esta acción no se puede deshacer.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setConfirmDel(null)} disabled={deleting} sx={{ color: Colors.textSecondary }}>Cancelar</Button>
            <Button onClick={confirmDelete} disabled={deleting} sx={{ color: Colors.error }}>
              {deleting ? "Eliminando..." : "Eliminar"}
            </Button>
          </DialogActions>
        </Dialog>

      </Container>
    </Box>
  );
}
